<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div
      v-if="fixedDepositSuccessCount.length || fixedDepositFailed.length"
      class="m-2"
    >
      <b-row>
        <b-col
          v-if="fixedDepositSuccessCount"
          md="6"
          class="mb-2"
        >
          <b-alert
            show
            variant="primary"
            class="p-2"
          >
            {{ fixedDepositSuccessCount }}
          </b-alert>
        </b-col>
        <b-col
          v-if="fixedDepositFailed.length"
          md="6"
          class="mb-2"
        >
          <div>
            <b-button variant="danger">
              {{ $t('FAILED LISTS') }}
            </b-button>
          </div>
          <b-table
            ref="fixedDepositFailedReport"
            :items="fixedDepositFailed"
            responsive
            :fields="failedHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          /></b-col>
      </b-row>
    </div>

    <div class="m-2">
      <b-row>
        <b-col
          md="6"
          class="mb-2"
        >
          <validation-observer ref="dataMigrationFdAccountRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select file (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col
          md="6"
        >
          <b-row>
            <b-col
              md="5"
              class="mt-2"
            >
              <Button
                v-if="getUserPermission('DATA_MIGRATION') && parseExcelFile.length"
                :label="$t('Create Fixed Deposit Account')"
                size="md"
                :loading="isLoading"
                @click="handleCreate"
              />
            </b-col>
            <b-col
              md="7"
              class="mt-2"
            >
              <Button
                class="ml-2"
                align="end"
                :label="$t('Download Fixed Deposit Migration Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <p
      v-if="totalSheetRows"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable, BFormFile, BButton, BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import XLSX from 'xlsx'
import Button from '../../../components/button/Button.vue'
import { toKobo } from '@/helpers/formatAmount'
import { batchAccountFormat, tableLessHeaders, tableAllHeaders } from '../../../../helpers/data'
import { toYearWithDate } from '../../../../helpers/formatDate'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    Button,
    BButton,
    BAlert,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      totalSheetRows: '',
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'fdAccountNumber', sortable: true, label: 'FD Account Number' },
        { key: 'branchCode', sortable: true },
        { key: 'productCode', sortable: true },
        { key: 'liquidationAccount', sortable: true, label: 'Liquidation Account Number' },
        { key: 'principalAmount', sortable: true, label: 'Fixed Deposit Amount' },
        { key: 'accruedInterestAmount', sortable: true, label: 'Accrued Interest Amount' },
        { key: 'tenure', sortable: true },
        { key: 'commencementDate', sortable: true, label: 'Start Date' },
        { key: 'applicableInterestRate', sortable: true, label: 'Interest Rate' },
        { key: 'partLiquidationCharge', sortable: true, label: 'Pre-Liquidation Charge Rate' },
        { key: 'applyRollover', sortable: true, label: 'Should Rollover?' },
        { key: 'rolloverOption', sortable: true },
      ],
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      fixedDepositSuccessCount: [],
      fixedDepositFailed: [],
      sortDesc: false,
      sortBy: 'newGLCode',
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
    }
  },
  methods: {
    ...mapActions({
      migrateFdAccountCreation: 'batch/migrateFdAccountCreation',
      downloadAccountFormat: 'batch/downloadAccountFormat',
    }),
    async handleCreate() {
      this.$refs.dataMigrationFdAccountRules.validate().then(success => {
        if (success) {
          this.confirm(this.migrateFdAccountAction, { text: this.$t('Are you sure you want to Create Fixed Deposit Migration?') })
        }
      })
    },
    async migrateFdAccountAction() {
      try {
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          fdAccountNumber: item.fdAccountNumber ? item.fdAccountNumber.toString() : null,
          branchCode: item.branchCode ? item.branchCode.toString() : null,
          productCode: item.productCode ? item.productCode.toString() : null,
          liquidationAccount: item.liquidationAccount ? item.liquidationAccount.toString() : null,
          principalAmount: item.principalAmount ? Number(toKobo(item.principalAmount).toFixed()) : null,
          accruedInterestAmount: item.accruedInterestAmount ? Number(toKobo(item.accruedInterestAmount).toFixed()) : null,
          tenure: item.tenure ? Number(item.tenure) : null,
          commencementDate: item.commencementDate ? toYearWithDate(item.commencementDate) : null,
          applicableInterestRate: item.applicableInterestRate ? Number(item.applicableInterestRate) : null,
          partLiquidationCharge: item.partLiquidationCharge ? Number(toKobo(item.partLiquidationCharge).toFixed()) : null,
          applyRollover: item.applyRollover.toLowerCase() === 'true' ? Boolean(true) : Boolean(false),
          rolloverOption: item.rolloverOption ? Number(item.rolloverOption) : null,
        }))

        const response = await this.migrateFdAccountCreation(formData)
        const { status, data } = response
        if (status === 200) {
          this.hasFileData = []
          this.parseExcelFile = []
          this.totalSheetRows = ''
          this.fixedDepositSuccessCount = data.success[0].message
          this.fixedDepositFailed = data.failed
          this.isDisabled = false
          this.isLoading = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const text = this.$t('Your request to create Batch fixed deposit migration failed. Reason')
          this.alertError({ text: `${text}: ${responseError.errorMessage}` })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.fileData = event.target.files ? event.target.files[0] : null
      if (!this.fileData) return

      const reader = new FileReader()
      reader.onload = e => this.processFile(e.target.result)
      reader.readAsBinaryString(this.fileData)
    },

    processFile(data) {
      const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'dd/mm/yyyy;@' })
      const sheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[sheetName]

      const rowObjects = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false })
      this.totalSheetRows = rowObjects.length
      this.parseExcelFile = this.transformRows(rowObjects)
    },

    transformRows(rows) {
      return rows.map(items => ({
        fdAccountNumber: items['FD Account Number'],
        branchCode: items['Branch Code'],
        productCode: items['Product Code'],
        liquidationAccount: items['Liquidation Account Number'],
        principalAmount: items['Fixed Deposit Amount'],
        accruedInterestAmount: items['Accrued Interest Amount'],
        tenure: items.Tenure,
        commencementDate: items['Start Date'],
        applicableInterestRate: items['Interest Rate'],
        partLiquidationCharge: items['Pre-liquidation Charge Rate'],
        applyRollover: items['Should Rollover?'],
        rolloverOption: items['Rollover Option'],
      }))
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Fixed Deposit Migration Format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const FIXED_DEPOSIT_ACCOUNT_MIGRATION_TEMPLATE = 12
        const response = await this.downloadAccountFormat(FIXED_DEPOSIT_ACCOUNT_MIGRATION_TEMPLATE)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Fd-Account-Data-Migration.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download data migration for Fixed Deposit Format was successful') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Your request to download data migration for Fixed Deposit Format failed. Reason')
          this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
